import { useModal } from "@ebay/nice-modal-react";
import { useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldSpaces from "components/FormFields/Spaces";
import FormFieldViewText from "components/FormFields/ViewText";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FeedbackActions from "ds/components/Feedback/Actions";
import TabWithCounter from "ds/components/Tab/WithCounter";
import TabPanel from "ds/components/TabPanel";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import { ManagedUserGroupIntegration } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { withTestId } from "utils/withTestId";

import { UserManagementActivationStatus } from "../../types";
import { UPDATE_GROUP_INTEGRATION_DRAWER_TEST_ID } from "./constants";
import styles from "./styles.module.css";
import { GroupsIntegrationsDrawerTabs, UpdateGroupIntegrationFields } from "./types";
import useUpdateGroup from "./useUpdateGroupIntegration";

type GroupsIntegrationsDetailsDrawerProps = {
  currentIntegration: ManagedUserGroupIntegration;
  activationStatus: UserManagementActivationStatus;
};

const ManageSlackAccessDetailsDrawer = createDrawer(
  ({ currentIntegration, activationStatus }: GroupsIntegrationsDetailsDrawerProps) => {
    const { onError, reportSuccess } = useTypedContext(FlashContext);
    const [currentTab, setCurrentTab] = useState("spaces");
    const drawer = useModal();

    const groupIntegrationUpdateForm = useForm<UpdateGroupIntegrationFields>({
      defaultValues: {
        spaces:
          currentIntegration.accessRules?.length > 0
            ? currentIntegration.accessRules
            : [{ space: undefined, spaceAccessLevel: undefined }],
      },
      mode: "onChange",
    });

    const {
      handleSubmit,
      formState: { isDirty },
    } = groupIntegrationUpdateForm;

    const { onUpdate } = useUpdateGroup();

    const onSubmit: SubmitHandler<UpdateGroupIntegrationFields> = (formData) => {
      const input = {
        id: currentIntegration.id,
        integrationType: currentIntegration.integrationType,
        integrationName: currentIntegration.integrationName,
        accessRules: formData.spaces,
        slackChannelID: currentIntegration.slackChannelID || "",
      };

      onUpdate(input)
        .then(({ data }) => {
          if (data) {
            reportSuccess({
              message: `Slack access was updated`,
            });

            drawer.hide();
          }
        })
        .catch(onError);
    };

    return (
      <DrawerForm isDirty={isDirty} {...withTestId(UPDATE_GROUP_INTEGRATION_DRAWER_TEST_ID)}>
        <FormProvider {...groupIntegrationUpdateForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle title="Access details" />
              <DrawerCloseIcon />
            </DrawerHeader>
            <DrawerBody>
              {activationStatus === UserManagementActivationStatus.INACTIVE && (
                <Banner variant="warning" title="Identity Access Management is inactive">
                  Access rules will not take effect until you activate Identity Access Management.
                  <FeedbackActions>
                    <LinkButton
                      to="/settings/management-strategy/"
                      analyticsPage={AnalyticsPageOrganization.OrganizationSlackAccess}
                      analyticsTitle="Callout Strategy Click"
                      variant="contrast"
                      size="small"
                    >
                      Choose strategy
                    </LinkButton>
                  </FeedbackActions>
                </Banner>
              )}

              <FormFieldViewText label="Name" value={currentIntegration.integrationName} />

              <FormFieldViewText
                label="Slack channel ID"
                value={currentIntegration.slackChannelID}
                withCopy
              />

              <Box direction="row" align="center" className={styles.tabs}>
                <TabWithCounter
                  onClick={setCurrentTab}
                  isActive={currentTab === GroupsIntegrationsDrawerTabs.Spaces}
                  id={GroupsIntegrationsDrawerTabs.Spaces}
                  label="Spaces"
                  count={currentIntegration.accessRules?.length}
                />
              </Box>

              <TabPanel
                isActive={currentTab === GroupsIntegrationsDrawerTabs.Spaces}
                id={GroupsIntegrationsDrawerTabs.Spaces}
              >
                <div className={styles.listWrapper}>
                  <FormFieldSpaces />
                </div>
              </TabPanel>

              <DrawerFooter>
                <DrawerFooterActions>
                  <DrawerCancelButton />
                  <Button variant="primary" type="submit">
                    Save changes
                  </Button>
                </DrawerFooterActions>
              </DrawerFooter>
            </DrawerBody>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showManageSlackAccessDetailsDrawer = createDrawerTrigger(
  ManageSlackAccessDetailsDrawer
);
